import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { writeFile, utils } from 'xlsx';
import LoadingSpinner from '../LoadingSpinner';
import { StyledLink } from '../Components';
import { Modal } from '../dialogs/useModal';
import { useConfig } from '../env';
import { GET_GUIDES_REPLACED_BY } from './guidesReplacedBy.graphql';

export interface GuidesReplacedByDialogProps {
  guideId: string;
  docName: string | null | undefined;
  docTitle: string | null | undefined;
}

function exportToExcel(rows: { docName?: string | null; docTitle?: string | null }[], docName: string | null | undefined) {
  const headings = ['Anvisning', 'Tittel'];
  const data = rows.map((row) => [row.docName ?? '', row.docTitle ?? '']);
  data.unshift(headings);
  const workbook = utils.book_new();
  const sheet = utils.aoa_to_sheet(data);
  utils.book_append_sheet(workbook, sheet, 'Erstattes av');
  writeFile(workbook, `anvisninger som erstattes av ${docName ?? ''}.xlsx`);
}

export function GuidesReplacedByDialog({ modal }: { modal: Modal<GuidesReplacedByDialogProps, unknown> }) {
  const { guideId, docName, docTitle } = modal.data;
  const { data, loading } = useQuery(GET_GUIDES_REPLACED_BY, { variables: { id: guideId } });
  const config = useConfig();

  const rows = data?.guidesReplacedBy ?? [];

  return (
    <Dialog open={true} onClose={() => modal.close()} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div" noWrap style={{ maxWidth: 900 }}>
          {`Anvisninger som erstattes av ${docName ?? ''} - ${docTitle ?? ''}`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <LoadingSpinner />}
        {!loading && rows.length === 0 && <Typography>Denne anvisningen erstatter ingen andre anvisninger.</Typography>}
        {!loading && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Anvisning</TableCell>
                <TableCell>Tittel</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <StyledLink to={`/guide/${row.id}`} target="_blank" rel="noopener noreferrer">
                      {row.docName}
                    </StyledLink>
                  </TableCell>
                  <TableCell>{row.docTitle}</TableCell>
                  <TableCell>
                    {config?.portalUrl && (
                      <Tooltip title="Åpne i byggforsk.no">
                        <IconButton
                          size="small"
                          href={`${config.portalUrl}/dokument/${row.docId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          component="a">
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <IconButton
          onClick={() => exportToExcel(rows, docName)}
          title="Eksportér til Excel"
          size="large"
          disabled={rows.length === 0}>
          <img src="/icons8-microsoft-excel.svg" alt="Excel icon" />
        </IconButton>
        <Button onClick={() => modal.close()} variant="contained">
          Lukk
        </Button>
      </DialogActions>
    </Dialog>
  );
}
